import { Anchor, Badge, Button, LoadingOverlay, Tooltip } from '@mantine/core';
import { IconPencil, IconStarFilled, IconTrash } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import UrgencyLogo from 'src/assets/logos/urgency.svg';
import { ContactAvatar } from 'src/Components/Contacts/ContactAvatar';
import { LinkedEntitiesSection } from 'src/Components/EntityLinks/LinkedEntitiesSection';
import { messagingChannelsConfig } from 'src/Components/Forms/Contact/PhysicalContactForm/messagingChannelsConfig';
import { paymentMethodsConfig } from 'src/Components/Forms/Contact/PhysicalContactForm/paymentMethodsConfig';
import { FavoriteButton } from 'src/Components/Shared/FavoriteButton';
import { RowActions } from 'src/Components/Shared/RowActions';
import { useIsAssetAboveQuotas } from 'src/Hooks/Shared/useIsAssetAboveQuotas';
import useIsMobile from 'src/Hooks/Shared/useIsMobile';
import useMailTo from 'src/Hooks/Shared/useMailTo';
import { LinkableEntityType } from 'src/Services/API/EntityLinks.api';
import { CompanyContactRole, ContactKind } from 'src/Services/API/generated';
import { prettyPrintPhoneNumber } from 'src/Services/Formatters/prettyPrintPhoneNumber';
import {
  formatSiret,
  getCompanyRoleLabel,
  getPaymentModeLabel,
} from 'src/Utils/companyContactRoles';
import { getContactName } from 'src/Utils/companyLogoDomain';

import styles from './PhysicalContactDetails.module.scss';
import { Props } from './PhysicalContactDetails.types';

// t('contactDrawer.paymentIdentifier.IBAN', 'Identifiant bancaire');
// t('contactDrawer.paymentIdentifier.LYDIA', 'Numéro de téléphone');
// t('contactDrawer.paymentIdentifier.WERO', 'Numéro de téléphone');
// t('contactDrawer.paymentIdentifier.PAYPAL', 'Adresse mail');

const PhysicalContactDetails = ({ contact, toggleFavorite, deleteContact, editContact }: Props) => {
  const { t } = useTranslation();

  const openMail = useMailTo({ email: contact?.email });

  const isMobile = useIsMobile();

  const isCompany = contact?.kind === ContactKind.Company;
  const interlocutors = contact?.interlocutors ?? [];
  const contactName = contact ? getContactName(contact) : '';

  const assetAboveQuotas = useIsAssetAboveQuotas();

  return contact ? (
    <div className={styles.container}>
      <div className={styles.header}>
        <ContactAvatar
          name={contactName}
          kind={contact.kind}
          imageUrl={contact.imageUrl}
          websiteUrl={contact.websiteUrl}
          emails={[contact.email]}
          size={isMobile ? 50 : 100}
        />
        <div className={styles.middleHeaderInfo}>
          <div className={styles.name}>{contactName}</div>
          {isCompany && contact.siret && (
            <div className={styles.siret}>
              {t('contactDrawer.siret', 'SIRET {{siret}}', { siret: formatSiret(contact.siret) })}
            </div>
          )}
          {(contact.category || contact.subcategory) && (
            <div className={styles.categoryBadges}>
              {contact.category && (
                <Badge variant="light" size="sm">
                  {t(`contacts.categories.${contact.category}`, contact.category)}
                </Badge>
              )}
              {contact.subcategory && (
                <Badge variant="outline" size="sm">
                  {t(`contacts.subcategories.${contact.subcategory}`, contact.subcategory)}
                </Badge>
              )}
            </div>
          )}
        </div>
        <div className={styles.headerActions}>
          {toggleFavorite && (
            <FavoriteButton
              disabled={assetAboveQuotas}
              isFavorite={contact.isFavorite}
              onChange={() =>
                toggleFavorite({ contactId: contact.id, isFavorite: !contact.isFavorite })
              }
            />
          )}
          {editContact && deleteContact && (
            <RowActions
              actions={[
                {
                  key: 'edit',
                  label: t('instructions.menu.edit', 'Modifier'),
                  icon: <IconPencil size={18} />,
                  disabled: assetAboveQuotas,
                  onClick: () => editContact(contact.id),
                },
                {
                  key: 'delete',
                  label: t('instructions.menu.delete', 'Supprimer'),
                  icon: <IconTrash size={18} />,
                  variant: 'danger',
                  onClick: () => deleteContact?.(contact.id),
                },
              ]}
            />
          )}
        </div>
      </div>
      <div className={styles.details}>
        {isCompany && interlocutors.length > 0 && (
          <div className={styles.detailSection}>
            <div className={styles.title}>{t('contactDrawer.interlocutors', 'Interlocuteurs')}</div>
            {interlocutors.map(interlocutor => (
              <div className={styles.interlocutor} key={interlocutor.id}>
                <div className={styles.interlocutorName}>
                  {interlocutor.displayName}
                  <Badge
                    variant={
                      interlocutor.companyRole === CompanyContactRole.Main ? 'light' : 'outline'
                    }
                    size="sm"
                  >
                    {getCompanyRoleLabel(
                      t,
                      interlocutor.companyRole,
                      interlocutor.companyRoleLabel
                    )}
                  </Badge>
                </div>
                {interlocutor.phoneNumber && (
                  <Anchor href={`tel:${interlocutor.phoneNumber}`} className={styles.value}>
                    {prettyPrintPhoneNumber(interlocutor.phoneNumber)}
                  </Anchor>
                )}
                {interlocutor.email && (
                  <Anchor href={`mailto:${interlocutor.email}`} className={styles.value}>
                    {interlocutor.email}
                  </Anchor>
                )}
              </div>
            ))}
          </div>
        )}
        <div className={styles.detailSection}>
          <div className={styles.title}>{t('contactDrawer.contact', 'contact')}</div>
          <div className={styles.keyValueLine}>
            <div className={styles.key}>{t('contactDrawer.phone', 'Téléphone')}</div>
            <div className={styles.value}>
              {contact.phoneNumber && (
                <Anchor href={`tel:${contact.phoneNumber}`} inherit>
                  {prettyPrintPhoneNumber(contact.phoneNumber)}
                </Anchor>
              )}
              {contact.isEmergencyContact && (
                <Tooltip
                  withArrow
                  label={t('contacts.emergencyContactTooltip', "Contact d'urgence")}
                >
                  <img src={UrgencyLogo} className={styles.urgencyLogo} />
                </Tooltip>
              )}
            </div>
          </div>
          <div className={styles.keyValueLine}>
            <div className={styles.key}>{t('contactDrawer.email', 'Email')}</div>
            <div className={styles.value}>{contact.email}</div>
          </div>
          {contact.address?.formattedAddress && (
            <div className={styles.keyValueLine}>
              <div className={styles.key}>{t('contactDrawer.address', 'Adresse')}</div>
              <div className={styles.value}>{contact.address?.formattedAddress}</div>
            </div>
          )}
          {contact.websiteUrl && (
            <div className={styles.keyValueLine}>
              <div className={styles.key}>{t('contactDrawer.website', 'Site web')}</div>
              <Anchor href={contact.websiteUrl} target="_blank" className={styles.value}>
                {contact.websiteUrl.replace(/^https?:\/\//, '')}
              </Anchor>
            </div>
          )}
          <div className={styles.keyValueLine}>
            <div className={styles.key}>{t('contactDrawer.channel', 'Canaux de messagerie')}</div>
            <div className={styles.channels}>
              {contact.messagingChannels.map(channel => {
                const channelInfo = messagingChannelsConfig.find(c => c.type === channel.type);
                if (!channelInfo) return <></>;

                const Icon = channelInfo.icon;
                const color = channelInfo.color;

                return (
                  <div className={styles.channel}>
                    <Icon size={16} color={color} />
                    <div>{t(`contacts.${channelInfo.label}`, channelInfo.label)}</div>
                    {channelInfo.type === 'MESSENGER' && (
                      <a
                        target="_blank"
                        href={`https://m.me/${channel.identifier}`}
                      >{`(${channel.identifier})`}</a>
                    )}
                  </div>
                );
              })}
              {contact.messagingChannels.length === 0 && (
                <div className={styles.value}>{t('contactDrawer.none', 'Aucun')}</div>
              )}
            </div>
          </div>
        </div>
        <div className={styles.detailSection}>
          <div className={styles.title}>
            {t('contactDrawer.bankingInformation', 'Informations bancaires')}
          </div>
          {contact.usualPaymentMode && (
            <div className={styles.keyValueLine}>
              <div className={styles.key}>
                {t('contactDrawer.usualPaymentMode', 'Mode de règlement habituel')}
              </div>
              <div className={styles.value}>{getPaymentModeLabel(t, contact.usualPaymentMode)}</div>
            </div>
          )}
          {contact.customerReference && (
            <div className={styles.keyValueLine}>
              <div className={styles.key}>
                {t('contactDrawer.customerReference', 'N° client / contrat')}
              </div>
              <div className={styles.value}>{contact.customerReference}</div>
            </div>
          )}
          <div className={styles.paymentMethodsList}>
            {contact.paymentMethods.map(paymentMethod => {
              const methodInfo =
                paymentMethodsConfig[paymentMethod.type as keyof typeof paymentMethodsConfig];

              return (
                <div className={styles.detailSection}>
                  <div className={styles.keyValueLine}>
                    <div className={styles.key}>
                      {t('contactDrawer.paymentReference', 'Référence de paiement')}
                    </div>
                    <div className={styles.methodReferenceRow}>
                      <img src={methodInfo.logo} className={styles.logo} />
                      <div className={styles.value}>
                        {methodInfo.label}
                        {paymentMethod.isFavorite && (
                          <Tooltip
                            label={t(
                              'contactDrawer.preferredPaymentMethod',
                              'Moyen de paiement préféré'
                            )}
                            withArrow
                          >
                            <IconStarFilled className={styles.star} size={20} />
                          </Tooltip>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className={styles.keyValueLine}>
                    <div className={styles.key}>
                      {t(`contactDrawer.paymentIdentifier.${paymentMethod.type}`)}
                    </div>
                    <div className={styles.value}>
                      {['LYDIA', 'WERO'].includes(paymentMethod.type)
                        ? prettyPrintPhoneNumber(paymentMethod.identifier)
                        : paymentMethod.identifier}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {contact.notes && (
        <div className={styles.detailSection}>
          <div className={styles.title}>{t('contactDrawer.notes', 'Notes')}</div>
          <div className={styles.notes}>{contact.notes}</div>
        </div>
      )}

      <LinkedEntitiesSection
        entityType={LinkableEntityType.Contact}
        entityId={contact.id}
        entityTitle={contact.displayName}
      />

      <div className={styles.border} />
      <div className={styles.buttons}>
        {deleteContact && (
          <Button
            onClick={() => {
              deleteContact(contact.id);
            }}
            variant="danger"
          >
            {t('common.delete', 'Supprimer')}
          </Button>
        )}

        <Button onClick={openMail} variant="outline" disabled={!contact.email}>
          {t('contacts.sendMail', 'Contacter par email')}
        </Button>
      </div>
    </div>
  ) : (
    <LoadingOverlay />
  );
};

export default PhysicalContactDetails;
